import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiOpenInNew } from '@mdi/js'
import type { Document as PdfDocument, Page as PdfPage, pdfjs as PdfJs } from 'react-pdf'
import { useAuthedObjectUrl } from '../../hooks/useAuthedObjectUrl'
import { ensureAuthCookie } from '../../api/client'
import { PreviewError } from './PreviewError'

const PAGE_BATCH = 3
const DEFAULT_WIDTH = 600

// The file reaches pdf.js as a `blob:` URL fetched with the bearer token
// (useAuthedObjectUrl, spec 2026-10-11-31), not through the access_token
// cookie: a stale cookie made the viewer fail on a 401 body.

// pdf.js loads through pdfjs-dist's worker build off unpkg, pinned to the
// exact version react-pdf ships (see front/package.json — react-pdf@11.0.0
// resolves pdfjs-dist@6.3.289). The other two pdf.js consumers in this
// codebase (DocumentSplitDialog.tsx, routes/$org/debug_.annotations.tsx)
// configure the same worker the same way.
//
// The import below is dynamic so `react-pdf` stays out of *this
// component's* static import graph — it does not by itself guarantee the
// library is absent from a page's bundle. DocumentSplitDialog.tsx and
// debug_.annotations.tsx both statically import react-pdf, so any page that
// already loads one of those (e.g. the document detail page) pulls react-pdf
// in regardless of this file. The lazy load only pays off on surfaces that
// don't already load those two — e.g. DocumentPreviewCard embedded on
// intervention/expense/contract pages.
let workerConfigured = false

export interface LoadedPdfModule {
  Document: typeof PdfDocument
  Page: typeof PdfPage
  pdfjs: typeof PdfJs
  /** Pass as `<Document options>`: one object for the module's lifetime, as react-pdf requires. */
  documentOptions: PdfDocumentOptions
}

/**
 * Where pdf.js fetches the assets it does not bundle, off the same unpkg
 * version as the worker. pdf.js 6 decodes CCITT fax and JBIG2 images (most
 * scanned PDFs) and JPEG 2000 through `wasm/`: without `wasmUrl` every such
 * image fails ("JBig2 failed to initialize") and each page renders blank
 * (issue #1519). `cmaps/` and `standard_fonts/` cover non-embedded fonts,
 * `iccs/` colour profiles.
 */
export interface PdfDocumentOptions {
  wasmUrl: string
  iccUrl: string
  cMapUrl: string
  cMapPacked: boolean
  standardFontDataUrl: string
}

export function pdfDocumentOptions(version: string): PdfDocumentOptions {
  const base = `https://unpkg.com/pdfjs-dist@${version}`
  return {
    wasmUrl: `${base}/wasm/`,
    iccUrl: `${base}/iccs/`,
    cMapUrl: `${base}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${base}/standard_fonts/`,
  }
}

let modulePromise: Promise<LoadedPdfModule> | null = null

export function loadPdfModule(): Promise<LoadedPdfModule> {
  if (!modulePromise) {
    modulePromise = Promise.all([
      import('react-pdf'),
      import('react-pdf/dist/Page/TextLayer.css'),
      import('react-pdf/dist/Page/AnnotationLayer.css'),
    ]).then(([m]) => {
      if (!workerConfigured) {
        m.pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${m.pdfjs.version}/build/pdf.worker.min.mjs`
        workerConfigured = true
      }
      return {
        Document: m.Document,
        Page: m.Page,
        pdfjs: m.pdfjs,
        documentOptions: pdfDocumentOptions(m.pdfjs.version),
      }
    })
  }
  return modulePromise
}

interface PdfPreviewProps {
  contentUrl: string
  name: string
  maxHeight: number
}

/**
 * PDF preview rendered with pdf.js (via react-pdf), imported dynamically so
 * it only downloads from *this* component's own code path once a `pdf`
 * document preview actually mounts (see the module-level comment above for
 * why that isn't a whole-page guarantee). Chosen over a bare `<iframe>`
 * (blank on mobile browsers with no PDF plugin, spec 2026-09-16-05) and over
 * the page-image endpoint (real text selection/search, one strategy for
 * every viewport instead of a mobile-only branch). Pages render lazily, a
 * batch at a time, and the "open document" link stays available as the
 * fallback whenever the viewer fails to initialise — the worker chunk fails
 * to load, or the file itself fails to parse.
 */
export function PdfPreview({ contentUrl, name, maxHeight }: PdfPreviewProps) {
  const { t } = useTranslation('documents')
  const [mod, setMod] = useState<LoadedPdfModule | null>(null)
  const [failed, setFailed] = useState(false)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [visibleCount, setVisibleCount] = useState(PAGE_BATCH)
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const { state: file, retry: retryFile } = useAuthedObjectUrl(contentUrl, 'application/pdf')

  useEffect(() => {
    let cancelled = false
    loadPdfModule()
      .then((loaded) => {
        if (!cancelled) setMod(loaded)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w) setWidth(Math.max(200, Math.floor(w)))
    })
    ro.observe(el)
    return () => ro.disconnect()
    // The container only mounts once the module and the file are loaded.
  }, [mod, file.status])

  const openLink = (
    <a
      href={contentUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => { void ensureAuthCookie() }}
      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      data-testid="document-preview-pdf-open-link"
    >
      <AppIcon path={mdiOpenInNew} size="xs" />
      {t('preview_card.open_document')}
    </a>
  )

  if (failed) {
    return (
      <div className="p-4 flex flex-col items-start gap-2" data-testid="document-preview-pdf-fallback">
        <p className="text-muted-foreground text-sm">{t('detail.pdf_preview_error')}</p>
        {openLink}
      </div>
    )
  }

  if (file.status === 'error') {
    return <PreviewError contentUrl={contentUrl} name={name} onRetry={retryFile} />
  }

  if (!mod || file.status !== 'ready') {
    return (
      <div className="p-4 flex flex-col items-start gap-2" data-testid="document-preview-pdf-loading">
        <p className="text-muted-foreground text-sm">{t('detail.pdf_preview_loading')}</p>
        {openLink}
      </div>
    )
  }

  const { Document, Page, documentOptions } = mod

  return (
    <div data-testid="document-preview-pdf" aria-label={name}>
      <div className="px-4 pt-4 pb-2">{openLink}</div>
      <div ref={containerRef} className="overflow-auto px-4 pb-4" style={{ maxHeight }}>
        <Document
          file={file.objectUrl}
          options={documentOptions}
          suspense={false}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={() => setFailed(true)}
          loading={<p className="text-muted-foreground text-sm">{t('detail.pdf_preview_loading')}</p>}
          error={<p className="text-muted-foreground text-sm">{t('detail.pdf_preview_error')}</p>}
        >
          {Array.from({ length: Math.min(numPages ?? 0, visibleCount) }, (_, i) => (
            <Page
              key={i}
              pageNumber={i + 1}
              width={width}
              className="mb-4 shadow-sm [&>canvas]:mx-auto"
              loading=""
              aria-label={t('page_highlight.page_alt', { page: i + 1 })}
            />
          ))}
        </Document>
        {numPages !== null && visibleCount < numPages && (
          <button
            type="button"
            onClick={() => setVisibleCount((v) => Math.min(numPages, v + PAGE_BATCH))}
            className="mt-2 text-sm text-primary hover:underline"
            data-testid="document-preview-pdf-show-more"
          >
            {t('detail.pdf_preview_show_more', { count: numPages - visibleCount })}
          </button>
        )}
      </div>
    </div>
  )
}
